import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';

const styles = {
  screen: {
    minHeight: '100vh',
    backgroundColor: '#0A2D5A',
    fontFamily: 'system-ui, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '40px',
    width: '100%',
    maxWidth: 560,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  },
  header: {
    textAlign: 'center',
    marginBottom: 32,
  },
  shield: {
    width: 72,
    height: 72,
    margin: '0 auto 16px',
    display: 'block',
  },
  brandName: {
    fontSize: 13,
    fontWeight: '700',
    color: '#E8620A',
    letterSpacing: 2,
    marginBottom: 8,
  },
  welcomeTitle: {
    fontSize: 26,
    fontWeight: '700',
    color: '#0A2D5A',
    marginBottom: 8,
  },
  welcomeSub: {
    fontSize: 15,
    color: '#666',
    lineHeight: 1.5,
  },
  divider: {
    height: 1,
    backgroundColor: '#eee',
    margin: '28px 0',
  },
  stepsLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#888',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 16,
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 20,
  },
  stepNum: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: '#E8620A',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
    fontWeight: '700',
    flexShrink: 0,
  },
  stepTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0A2D5A',
    marginBottom: 4,
  },
  stepDesc: {
    fontSize: 13,
    color: '#666',
    lineHeight: 1.5,
  },
  infoBox: {
    backgroundColor: '#F4F6F9',
    borderRadius: 10,
    padding: '16px 20px',
    marginTop: 8,
    marginBottom: 28,
    borderLeft: '4px solid #1D6FA4',
  },
  infoText: {
    fontSize: 13,
    color: '#444',
    lineHeight: 1.6,
  },
  button: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#E8620A',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
  },
  skipBtn: {
    width: '100%',
    padding: '10px',
    backgroundColor: 'transparent',
    color: '#888',
    border: 'none',
    fontSize: 13,
    cursor: 'pointer',
    marginTop: 8,
  },
};

export default function Welcome({ agentName, onComplete }) {
  const navigate = useNavigate();
  const firstName = (agentName || '').split(' ')[0] || 'there';

  const handleGetStarted = async () => {
    try {
      const agentId = auth.currentUser?.uid;
      if (agentId) {
        await setDoc(doc(db, 'agents', agentId), { hasSeenWelcome: true }, { merge: true });
      }
    } catch (err) {
      console.error('Error setting welcome flag:', err);
    }
    onComplete();
    navigate('/profile');
  };

  const handleSkip = async () => {
    try {
      const agentId = auth.currentUser?.uid;
      if (agentId) {
        await setDoc(doc(db, 'agents', agentId), { hasSeenWelcome: true }, { merge: true });
      }
    } catch (err) {
      console.error('Error setting welcome flag:', err);
    }
    onComplete();
    navigate('/');
  };

  return (
    <div style={styles.screen}>
      <div style={styles.card}>

        <div style={styles.header}>
          <img src="/icon.png" alt="Medicare Plan Guardian" style={styles.shield} />
          <div style={styles.brandName}>MEDICARE PLAN GUARDIAN</div>
          <div style={styles.welcomeTitle}>Welcome, {firstName}.</div>
          <div style={styles.welcomeSub}>
            Your portal is ready. Setup takes about 10 minutes and you only do it once.
          </div>
        </div>

        <div style={styles.divider} />

        <div style={styles.stepsLabel}>Three Steps to Get Started</div>

        <div style={styles.step}>
          <div style={styles.stepNum}>1</div>
          <div>
            <div style={styles.stepTitle}>Complete Your Profile</div>
            <div style={styles.stepDesc}>
              Upload your headshot, add your phone number, and paste your Calendly link.
              This information appears in every client's app.
            </div>
          </div>
        </div>

        <div style={styles.step}>
          <div style={styles.stepNum}>2</div>
          <div>
            <div style={styles.stepTitle}>Add Your First Client</div>
            <div style={styles.stepDesc}>
              Enter the client's name, mobile number, plan type, carrier, and start date.
              Takes about 2 minutes per client.
            </div>
          </div>
        </div>

        <div style={styles.step}>
          <div style={styles.stepNum}>3</div>
          <div>
            <div style={styles.stepTitle}>Send the App Link</div>
            <div style={styles.stepDesc}>
              Copy the personalized link from My Clients and text it to your client.
              They tap it, install the app, and your face is on their phone.
            </div>
          </div>
        </div>

        <div style={styles.infoBox}>
          <div style={styles.infoText}>
            After setup, Medicare Plan Guardian runs on its own. Every AEP alert, Part D
            reminder, annual review notice, and birthday message fires automatically —
            every year — with your name and contact info in every notification.
            You set it up once and it works forever.
          </div>
        </div>

        <button style={styles.button} onClick={handleGetStarted}>
          Get Started — Set Up My Profile
        </button>
        <button style={styles.skipBtn} onClick={handleSkip}>
          Skip for now — go to Dashboard
        </button>

      </div>
    </div>
  );
}